import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Container,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import { useNavigate } from 'react-router-dom'
import { AgentService } from '../services/agent.service'
import { useAgent } from '../store/agent.store'
import { useAuth } from '../store/auth.store'
import { useArea } from '../store/area.store'
import { resetCustomerScopedState } from '../utils/sessionReset'
import URLS from '../enums/route'
import { themeColors } from '../styles'

const PER_PAGE = 30

type RegisteredFilter = 'all' | 'registered' | 'unregistered'

const FILTER_TO_FLAG: Record<RegisteredFilter, boolean | null> = {
  all: null,
  registered: true,
  unregistered: false,
}

const AgentCustomersPage = () => {
  const navigate = useNavigate()
  const { agent, setImpersonating, clearAgent } = useAgent()
  const { setUser, logout } = useAuth()
  const { setChoosedArea } = useArea()

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<RegisteredFilter>('all')
  const [customers, setCustomers] = useState<IAgentCustomer[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [entering, setEntering] = useState<string | null>(null)
  const [error, setError] = useState('')

  /**
   * `append` distinguishes "load more" from a fresh search: appending keeps the
   * rows already on screen, a fresh query replaces them. Without it, paging
   * would wipe the list on every click.
   */
  const load = useCallback(
    async (term: string, pageToLoad: number, reg: RegisteredFilter, append: boolean) => {
      append ? setLoadingMore(true) : setLoading(true)
      setError('')
      try {
        const response = await AgentService.customers(term, pageToLoad, PER_PAGE, FILTER_TO_FLAG[reg])
        if (response.status === 'success') {
          setCustomers(prev => (append ? [...prev, ...response.items] : response.items))
          setTotal(response.total)
          setPage(pageToLoad)
        } else {
          setError('שגיאה בטעינת לקוחות')
        }
      } catch (e) {
        console.error('[AGENT CUSTOMERS]', e)
        setError('שגיאה בטעינת לקוחות')
      } finally {
        append ? setLoadingMore(false) : setLoading(false)
      }
    },
    []
  )

  // Debounced so typing a company name does not fire a request per keystroke.
  // Any change to the term or the filter restarts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      load(search, 1, filter, false)
    }, 350)
    return () => clearTimeout(timer)
  }, [search, filter, load])

  // Mirrors AuthProvider.logoutUser, which the customers page had no way to
  // reach: clear the agent's own JWT/refresh token AND any auth state, drop the
  // session date, and return to the login screen. These are shared machines, so
  // nothing may be left behind in localStorage.
  const handleLogout = () => {
    clearAgent()
    logout()
    localStorage.removeItem('lastLoginDate')
    navigate(URLS.AuthHome.LINK)
  }

  const enterCustomer = async (customer: IAgentCustomer) => {
    setEntering(customer.extId)
    setError('')
    try {
      const response = await AgentService.impersonate(customer.extId)
      if (response.status !== 'success' || !response.user || !response.session) {
        setError(response.message || 'לא ניתן להתחבר ללקוח זה')
        return
      }

      // Clear the previous customer's state BEFORE writing the new one, or the
      // old chosen area survives and the catalog comes back wrong.
      resetCustomerScopedState()
      setUser(response.user)
      setImpersonating(response.session)

      // Same area rule as a real customer login (see LoginForm).
      if (response.area && response.area.length === 1) {
        setChoosedArea(response.area[0])
        navigate(URLS.HOME.LINK)
      } else {
        navigate(URLS.AREA_PAGE.LINK)
      }
    } catch (e) {
      console.error('[AGENT IMPERSONATE]', e)
      setError('שגיאה בהתחברות ללקוח')
    } finally {
      setEntering(null)
    }
  }

  const hasMore = customers.length < total

  return (
    <Container sx={{ pt: 3, pb: 6 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography fontWeight={700} fontSize={20} color={themeColors.title} mb={0.5}>
            בחירת לקוח
          </Typography>
          <Typography fontSize={14} color={themeColors.title}>
            {agent?.name ? `שלום ${agent.name}` : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
        >
          התנתקות
        </Button>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="חיפוש לפי שם, חברה, מספר לקוח או טלפון"
        value={search}
        onChange={event => setSearch(event.target.value)}
      />

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={filter}
        onChange={(_event, value: RegisteredFilter | null) => {
          // MUI passes null when the active button is clicked again; keeping the
          // current filter is less surprising than clearing it.
          if (value) {
            setFilter(value)
          }
        }}
        sx={{ mt: 2 }}
      >
        <ToggleButton value="all">הכל</ToggleButton>
        <ToggleButton value="registered">רשומים</ToggleButton>
        <ToggleButton value="unregistered">לא רשומים</ToggleButton>
      </ToggleButtonGroup>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {total > 0 && (
            <Typography fontSize={13} color={themeColors.title} mt={2}>
              {`מוצגים ${customers.length} מתוך ${total}`}
            </Typography>
          )}

          <List>
            {customers.map(customer => (
              <ListItem
                key={customer.extId}
                divider
                secondaryAction={
                  <Button
                    variant="contained"
                    disabled={customer.isBlocked || entering === customer.extId}
                    onClick={() => enterCustomer(customer)}
                  >
                    {entering === customer.extId ? '...' : 'כניסה'}
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <span>{customer.company || customer.name || customer.extId}</span>
                      {customer.isBlocked && <Chip size="small" color="error" label="חסום" />}
                      {!customer.isRegistered && <Chip size="small" label="לא רשום" />}
                    </Box>
                  }
                  secondary={[customer.extId, customer.city, customer.phone].filter(Boolean).join(' · ')}
                />
              </ListItem>
            ))}
            {!customers.length && (
              <Typography py={4} textAlign="center" color={themeColors.title}>
                לא נמצאו לקוחות
              </Typography>
            )}
          </List>

          {hasMore && (
            <Box display="flex" justifyContent="center" pb={2}>
              <Button
                variant="outlined"
                disabled={loadingMore}
                onClick={() => load(search, page + 1, filter, true)}
              >
                {loadingMore ? '...' : 'טען עוד'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  )
}

export default AgentCustomersPage
